import getSessionId from './analytics';

interface BackendAnalyticsEvent {
  eventType: 'pageview' | 'event' | 'error' | 'timing' | 'user_action';
  eventData: Record<string, any>;
  timestamp: string;
  sessionId: string;
}

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

// Queue for offline analytics
let analyticsQueue: BackendAnalyticsEvent[] = [];
let isOnline = navigator.onLine;

// Handle online/offline status
window.addEventListener('online', () => {
  isOnline = true;
  processQueue();
});

window.addEventListener('offline', () => {
  isOnline = false;
});

// Process queued events
const processQueue = async () => {
  if (analyticsQueue.length === 0) return;
  
  const events = [...analyticsQueue];
  analyticsQueue = [];
  
  for (const event of events) {
    await sendAnalyticsEvent(event);
  }
};

// Send analytics event to backend
const sendAnalyticsEvent = async (event: BackendAnalyticsEvent): Promise<void> => {
  try {
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${BACKEND_URL}/api/analytics`, {
      method: 'POST',
      headers,
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      // Silently handle server errors and CORS issues
      if (response.status === 0 || response.status >= 400 || !isOnline) {
        // Don't queue events for CORS/auth errors to prevent memory buildup
        return;
      }
    }
  } catch (error: any) {
    // Completely silence CORS and network errors
    if (error.message?.includes('CORS') || 
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.name === 'TypeError' ||
        error.message?.includes('Load failed')) {
      // Silently drop the event - don't queue to prevent memory issues
      return;
    } else {
      // Only log non-network errors
      console.debug('Backend analytics error:', error.message);
    }
  }
};

// Track user interactions that Google Analytics might miss
export const trackUserInteraction = (
  element: string,
  action: string,
  details?: Record<string, any>
): void => {
  const event: BackendAnalyticsEvent = {
    eventType: 'user_action',
    eventData: {
      element,
      action,
      ...details,
      url: window.location.href,
      userAgent: navigator.userAgent,
    },
    timestamp: new Date().toISOString(),
    sessionId: sessionStorage.getItem('analyticsSessionId') || 'anonymous',
  };

  sendAnalyticsEvent(event);
};

// Track performance metrics
export const trackPerformance = (
  metric: string,
  value: number,
  details?: Record<string, any>
): void => {
  const event: BackendAnalyticsEvent = {
    eventType: 'timing',
    eventData: {
      metric,
      value,
      ...details,
      url: window.location.href,
    },
    timestamp: new Date().toISOString(),
    sessionId: sessionStorage.getItem('analyticsSessionId') || 'anonymous',
  };

  sendAnalyticsEvent(event);
};

// Track errors that Google Analytics might miss
export const trackError = (
  error: Error,
  context: Record<string, any> = {}
): void => {
  const event: BackendAnalyticsEvent = {
    eventType: 'error',
    eventData: {
      message: error.message,
      stack: error.stack,
      ...context,
      url: window.location.href,
      userAgent: navigator.userAgent,
    },
    timestamp: new Date().toISOString(),
    sessionId: sessionStorage.getItem('analyticsSessionId') || 'anonymous',
  };

  sendAnalyticsEvent(event);
};

// Track custom metrics for admin panel
export const trackAdminMetric = (
  metric: string,
  value: any,
  category: string = 'general'
): void => {
  const event: BackendAnalyticsEvent = {
    eventType: 'event',
    eventData: {
      category: `admin_${category}`,
      metric,
      value,
      adminUser: true,
      url: window.location.href,
    },
    timestamp: new Date().toISOString(),
    sessionId: sessionStorage.getItem('analyticsSessionId') || 'anonymous',
  };

  sendAnalyticsEvent(event);
};

// Track API response times
export const trackApiPerformance = (
  endpoint: string,
  duration: number,
  status: number,
  method: string = 'GET'
): void => {
  const event: BackendAnalyticsEvent = {
    eventType: 'timing',
    eventData: {
      category: 'api_performance',
      endpoint,
      duration,
      status,
      method,
      url: window.location.href,
    },
    timestamp: new Date().toISOString(),
    sessionId: sessionStorage.getItem('analyticsSessionId') || 'anonymous',
  };

  sendAnalyticsEvent(event);
};

// Track feature usage
export const trackFeatureUsage = (
  feature: string,
  action: string,
  details?: Record<string, any>
): void => {
  const event: BackendAnalyticsEvent = {
    eventType: 'event',
    eventData: {
      category: 'feature_usage',
      feature,
      action,
      ...details,
      url: window.location.href,
    },
    timestamp: new Date().toISOString(),
    sessionId: sessionStorage.getItem('analyticsSessionId') || 'anonymous',
  };

  sendAnalyticsEvent(event);
};

// Process any queued events on load
if (isOnline) {
  processQueue();
}